import { Box, CircularProgress, Paper, Typography } from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { LineChart } from '@mui/x-charts/LineChart'
import dayjs from 'dayjs'
import { useGetFirebaseDailyUsersQuery, useGetFirebaseAnalyticsQuery } from 'store/dashboard'
import InfoTooltip from 'components/InfoTooltip'
import { useTimeRange } from './TimeRangeContext'

const ChangeChip = ({ value }: { value: number }) => {
  const positive = value >= 0
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.3,
        px: 0.8, py: 0.2, borderRadius: 4,
        bgcolor: positive ? '#E8F5E9' : '#FFEBEE',
        color: positive ? '#2E7D32' : '#C62828',
        fontSize: 11, fontWeight: 700, width: 'fit-content'
      }}
    >
      {positive ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : <TrendingDownIcon sx={{ fontSize: 12 }} />}
      {positive ? '+' : ''}{value.toFixed(1)}%
    </Box>
  )
}

const formatGa4Date = (d: string) =>
  d.length === 8 ? dayjs(d, 'YYYYMMDD').format('MMM D') : d

const groupByQuarter = (data: Array<{ date: string; activeUsers: number }>) => {
  const map: Record<string, number> = {}
  data.forEach(({ date, activeUsers }) => {
    const d = date.length === 8 ? dayjs(date, 'YYYYMMDD') : dayjs(date)
    const q = Math.ceil((d.month() + 1) / 3)
    const key = `Q${q} ${d.year()}`
    map[key] = (map[key] ?? 0) + activeUsers
  })
  return Object.entries(map).map(([label, value]) => ({ label, value }))
}

const ActiveUsersChart = () => {
  const { startDate, endDate } = useTimeRange()

  const { data: daily, isLoading: dailyLoading, isError: dailyError } =
    useGetFirebaseDailyUsersQuery({ startDate, endDate })

  const { data: firebase, isLoading: firebaseLoading } =
    useGetFirebaseAnalyticsQuery({ startDate, endDate })

  const isLoading = dailyLoading || firebaseLoading
  const unconfigured = daily && !daily.isConfigured

  const rangeInDays = dayjs(endDate).diff(dayjs(startDate), 'day')
  const isQuarterly = rangeInDays > 90

  const chartPoints = !daily?.data
    ? []
    : isQuarterly
    ? groupByQuarter(daily.data)
    : daily.data.map((d) => ({ label: formatGa4Date(d.date), value: d.activeUsers }))

  const header = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant='overline'
          color='text.secondary'
          sx={{ letterSpacing: 1.5 }}
        >
          ACTIVE USERS
        </Typography>
        <InfoTooltip title='The trend of active users over time' iconSize={14} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
        <Typography variant='h3' sx={{ fontWeight: 800, color: '#002C5F' }}>
          {(firebase?.activeUsers ?? 0).toLocaleString()}
        </Typography>
        {firebase !== undefined && (
          <ChangeChip value={firebase.activeUsersChangePercent} />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant='caption' color='text.secondary'>{'↑ Users'}</Typography>
        {isQuarterly && (
          <Typography variant='caption' color='text.disabled' sx={{ fontSize: 10 }}>
            Quarterly View
          </Typography>
        )}
      </Box>
    </>
  )

  return (
    <Paper
      sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 1, boxShadow: 'none' }}
      elevation={0}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : unconfigured ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography color='text.disabled'>Firebase not configured</Typography>
        </Box>
      ) : dailyError || !daily || chartPoints.length === 0 ? (
        <>
          {header}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, mt: 2 }}>
            <Typography variant='body2' color='text.secondary'>No trend data for selected period</Typography>
          </Box>
        </>
      ) : (
        <>
          {header}
          <Box sx={{ width: '100%', height: 220, mt: 2 }}>
            <LineChart
              xAxis={[{
                scaleType: 'point',
                data: chartPoints.map((p) => p.label),
              }]}
              series={[
                { label: 'Active Users', data: chartPoints.map((p) => p.value), color: '#002C5F' }
              ]}
              slotProps={{ legend: { hidden: true } }}
              height={220}
            />
          </Box>
        </>
      )}
    </Paper>
  )
}

export default ActiveUsersChart
